import { motion, AnimatePresence } from "framer-motion";
import { 
  Monitor, 
  Search, 
  Calendar, 
  UserCircle, 
  Settings, 
  BarChart3,
  Zap,
  TrendingUp,
  CopyX
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/abstract_digital_patient_intelligence_background.png";
const appScreen = "/assets/cards/patient360_unified_front_desk_dashboard_interface.png";

export default function Patient360Card() {
  const features = [
    {
      icon: Monitor,
      title: "Unified Front Desk Command Center",
      desc: "Single-screen access to check-ins, arrivals, appointments, and patient status across all locations"
    },
    {
      icon: Search,
      title: "AI-Powered Patient Search",
      desc: "Find any patient instantly using name, DOB, phone, MRN, or even partial/fuzzy matches"
    },
    {
      icon: Calendar,
      title: "Smart Scheduling Engine",
      desc: "Drag-and-drop appointment management with conflict detection and provider availability"
    },
    {
      icon: UserCircle,
      title: "360° Patient Profile",
      desc: "Complete view of demographics, insurance, visit history, balances, and documents"
    },
    {
      icon: Settings,
      title: "Patient Management Workflows",
      desc: "Registration, updates, merges, and deduplication with AI-assisted data quality checks"
    }
  ];

  const kpis = [
    { label: "Patient Lookup Time", value: "< 1 sec", icon: Search, color: "text-emerald-400" },
    { label: "Registration Speed", value: "-55%", icon: Zap, color: "text-blue-400" },
    { label: "Scheduling Efficiency", value: "+40%", icon: Calendar, color: "text-pink-400" },
    { label: "Front Desk Productivity", value: "+45%", icon: TrendingUp, color: "text-amber-400" },
    { label: "Duplicate Records", value: "-90%", icon: CopyX, color: "text-purple-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            Patient360™
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            Patient360™
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            Complete Patient Intelligence at Your Fingertips
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "One unified view. Every patient detail. Instant AI-powered search that finds who you need in milliseconds."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>

          {/* Floating Interface Screen */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
            className="relative z-10 w-[95%] max-w-xl aspect-video bg-slate-950 rounded-xl border border-slate-700 shadow-2xl overflow-hidden -mt-8"
          >
             {/* Fake Browser Toolbar */}
             <div className="h-10 bg-slate-900 flex items-center px-4 gap-3 border-b border-slate-800">
               <div className="flex gap-2">
                 <div className="w-3 h-3 rounded-full bg-slate-700" />
                 <div className="w-3 h-3 rounded-full bg-slate-700" />
                 <div className="w-3 h-3 rounded-full bg-slate-700" />
               </div>
               <div className="flex-1 flex justify-center">
                  <div className="bg-slate-800 rounded px-4 py-1.5 text-xs text-slate-400 flex items-center gap-2 w-72">
                    <Search className="w-3.5 h-3.5" /> Search patients...
                  </div>
               </div>
             </div>

             {/* Screen Content */}
             <img 
              src={appScreen} 
              alt="Patient360 Dashboard" 
              className="w-full h-full object-cover"
            />
             
             {/* Screen Glare */}
             <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none z-20" />
          </motion.div>

          {/* Floating Context Cards */}
          <div className="absolute inset-0 z-20 pointer-events-none">
             
             {/* Search Result - Top Left */}
             <motion.div
               animate={{ y: [0, -10, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
               className="absolute top-[18%] left-[8%] bg-white/95 backdrop-blur text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[230px]"
             >
               <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                 <Search className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500 uppercase">Instant Match</div>
                 <div className="text-base font-bold">Sarah Jones (DOB: 1985)</div>
               </div>
             </motion.div>

             {/* Profile Stats - Bottom Right */}
             <motion.div
               animate={{ x: [0, 5, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
               className="absolute bottom-[30%] right-[4%] bg-white/95 backdrop-blur text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[220px]"
             >
               <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 shrink-0">
                 <UserCircle className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500 uppercase">360° Profile</div>
                 <div className="text-base font-bold">History • Insurance • Labs</div>
               </div>
             </motion.div>

          </div>

          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

